/**
 * Per-field help for the remaining provider channels (Twilio WhatsApp/SMS,
 * Telegram, Viber, LINE and the team-chat webhooks). Same pattern as
 * MetaFieldHelp / SendGridFieldHelp: an info icon that opens a popover with the
 * URL and steps to obtain each value. Bilingual (es/en), follows the UI locale.
 *
 * Content is keyed by "<channel>.<field>" (or "teamchat.<platform>").
 */

import React from "react";
import { Box, Link, Typography } from "@mui/material";
import { useLocaleState } from "react-admin";
import { HelpTooltip } from "@/components/common";

type Locale = "es" | "en";

interface FieldHelp {
  title: string;
  steps: string[];
  url: string;
  note?: string;
}

const HELP: Record<string, Record<Locale, FieldHelp>> = {
  // ---- WhatsApp (Twilio) ----
  "twilioWa.apiEndpoint": {
    es: {
      title: "API Endpoint (Twilio)",
      steps: [
        "Endpoint base de la API de Twilio.",
        "Valor por defecto: https://api.twilio.com — no suele cambiar.",
      ],
      url: "https://console.twilio.com",
    },
    en: {
      title: "API Endpoint (Twilio)",
      steps: [
        "Twilio API base endpoint.",
        "Default: https://api.twilio.com — rarely changes.",
      ],
      url: "https://console.twilio.com",
    },
  },
  "twilioWa.apiKey": {
    es: {
      title: "API Key (Twilio)",
      steps: [
        "Es el Account SID de Twilio (o el SID de una API Key).",
        "Twilio Console → panel principal → «Account Info» → Account SID (empieza por AC…).",
      ],
      url: "https://console.twilio.com",
    },
    en: {
      title: "API Key (Twilio)",
      steps: [
        "The Twilio Account SID (or an API Key SID).",
        "Twilio Console → dashboard → «Account Info» → Account SID (starts with AC…).",
      ],
      url: "https://console.twilio.com",
    },
  },
  "twilioWa.apisecret": {
    es: {
      title: "API Secret (Twilio)",
      steps: [
        "Es el Auth Token de Twilio (o el Secret de la API Key).",
        "Twilio Console → «Account Info» → Auth Token (pulsa para revelarlo).",
      ],
      note: "En edición, deja el campo vacío para conservar el valor actual.",
      url: "https://console.twilio.com",
    },
    en: {
      title: "API Secret (Twilio)",
      steps: [
        "The Twilio Auth Token (or the API Key Secret).",
        "Twilio Console → «Account Info» → Auth Token (click to reveal).",
      ],
      note: "On edit, leave this field empty to keep the current value.",
      url: "https://console.twilio.com",
    },
  },
  "twilioWa.businessPhoneNumber": {
    es: {
      title: "Business Phone Number",
      steps: [
        "El número de Twilio habilitado para WhatsApp.",
        "Twilio Console → Messaging → «Senders» → WhatsApp senders.",
      ],
      url: "https://console.twilio.com/us1/develop/sms/senders/whatsapp-senders",
    },
    en: {
      title: "Business Phone Number",
      steps: [
        "The Twilio number enabled for WhatsApp.",
        "Twilio Console → Messaging → «Senders» → WhatsApp senders.",
      ],
      url: "https://console.twilio.com/us1/develop/sms/senders/whatsapp-senders",
    },
  },

  // ---- SMS (Twilio) ----
  "sms.apiEndpoint": {
    es: {
      title: "API Endpoint (Twilio)",
      steps: [
        "Endpoint base de la API de Twilio.",
        "Valor por defecto: https://api.twilio.com — no suele cambiar.",
      ],
      url: "https://console.twilio.com",
    },
    en: {
      title: "API Endpoint (Twilio)",
      steps: [
        "Twilio API base endpoint.",
        "Default: https://api.twilio.com — rarely changes.",
      ],
      url: "https://console.twilio.com",
    },
  },
  "sms.apiKey": {
    es: {
      title: "API Key (Twilio)",
      steps: [
        "Es el Account SID de Twilio (o el SID de una API Key).",
        "Twilio Console → «Account Info» → Account SID (empieza por AC…).",
      ],
      url: "https://console.twilio.com",
    },
    en: {
      title: "API Key (Twilio)",
      steps: [
        "The Twilio Account SID (or an API Key SID).",
        "Twilio Console → «Account Info» → Account SID (starts with AC…).",
      ],
      url: "https://console.twilio.com",
    },
  },
  "sms.apisecret": {
    es: {
      title: "API Secret (Twilio)",
      steps: [
        "Es el Auth Token de Twilio (o el Secret de la API Key).",
        "Twilio Console → «Account Info» → Auth Token.",
      ],
      note: "En edición, deja el campo vacío para conservar el valor actual.",
      url: "https://console.twilio.com",
    },
    en: {
      title: "API Secret (Twilio)",
      steps: [
        "The Twilio Auth Token (or the API Key Secret).",
        "Twilio Console → «Account Info» → Auth Token.",
      ],
      note: "On edit, leave this field empty to keep the current value.",
      url: "https://console.twilio.com",
    },
  },
  "sms.senderId": {
    es: {
      title: "Sender Id",
      steps: [
        "El número de Twilio remitente (o un Messaging Service SID / sender ID alfanumérico).",
        "Twilio Console → Phone Numbers → Manage → Active numbers.",
      ],
      note: "Un sender ID alfanumérico NO puede recibir respuestas.",
      url: "https://console.twilio.com/us1/develop/phone-numbers/manage/incoming",
    },
    en: {
      title: "Sender Id",
      steps: [
        "The sending Twilio number (or a Messaging Service SID / alphanumeric sender ID).",
        "Twilio Console → Phone Numbers → Manage → Active numbers.",
      ],
      note: "An alphanumeric sender ID CANNOT receive replies.",
      url: "https://console.twilio.com/us1/develop/phone-numbers/manage/incoming",
    },
  },

  // ---- Telegram ----
  "telegram.botToken": {
    es: {
      title: "Bot Token",
      steps: [
        "En Telegram, abre un chat con @BotFather.",
        "Envía /newbot y sigue los pasos (nombre + username).",
        "BotFather te devuelve el token (formato 123456:ABC…). Cópialo.",
      ],
      url: "https://t.me/BotFather",
    },
    en: {
      title: "Bot Token",
      steps: [
        "In Telegram, open a chat with @BotFather.",
        "Send /newbot and follow the prompts (name + username).",
        "BotFather returns the token (format 123456:ABC…). Copy it.",
      ],
      url: "https://t.me/BotFather",
    },
  },

  // ---- Viber ----
  "viber.botToken": {
    es: {
      title: "Viber Auth Token",
      steps: [
        "Entra en el Viber Admin Panel (partners.viber.com) y crea una cuenta de bot.",
        "En la configuración del bot, copia el «authentication token».",
      ],
      url: "https://partners.viber.com",
    },
    en: {
      title: "Viber Auth Token",
      steps: [
        "Go to the Viber Admin Panel (partners.viber.com) and create a bot account.",
        "In the bot settings, copy the «authentication token».",
      ],
      url: "https://partners.viber.com",
    },
  },
  "viber.senderName": {
    es: {
      title: "Sender Name",
      steps: [
        "Nombre que se muestra como remitente en Viber (máx. 28 caracteres).",
        "Se define al crear el bot en el Viber Admin Panel.",
      ],
      url: "https://partners.viber.com",
    },
    en: {
      title: "Sender Name",
      steps: [
        "Name shown as the sender in Viber (max 28 characters).",
        "Set when creating the bot in the Viber Admin Panel.",
      ],
      url: "https://partners.viber.com",
    },
  },

  // ---- LINE ----
  "line.channelAccessToken": {
    es: {
      title: "Channel Access Token",
      steps: [
        "LINE Developers Console → tu Provider → canal «Messaging API».",
        "Pestaña «Messaging API» → emite un «Channel access token» (long-lived).",
        "Cópialo.",
      ],
      url: "https://developers.line.biz/console/",
    },
    en: {
      title: "Channel Access Token",
      steps: [
        "LINE Developers Console → your Provider → «Messaging API» channel.",
        "«Messaging API» tab → issue a long-lived «Channel access token».",
        "Copy it.",
      ],
      url: "https://developers.line.biz/console/",
    },
  },

  // ---- Team chat webhooks ----
  "teamchat.slack": {
    es: {
      title: "Webhook de Slack",
      steps: [
        "api.slack.com/apps → crea (o abre) una app en tu workspace.",
        "«Incoming Webhooks» → actívalo → «Add New Webhook to Workspace» → elige el canal.",
        "Copia la Webhook URL (https://hooks.slack.com/services/…).",
      ],
      url: "https://api.slack.com/messaging/webhooks",
    },
    en: {
      title: "Slack webhook",
      steps: [
        "api.slack.com/apps → create (or open) an app in your workspace.",
        "«Incoming Webhooks» → enable → «Add New Webhook to Workspace» → pick the channel.",
        "Copy the Webhook URL (https://hooks.slack.com/services/…).",
      ],
      url: "https://api.slack.com/messaging/webhooks",
    },
  },
  "teamchat.teams": {
    es: {
      title: "Webhook de Microsoft Teams",
      steps: [
        "En el canal de Teams: «…» → Conectores → «Incoming Webhook» → Configurar.",
        "Ponle nombre, crea y copia la URL.",
        "En Teams nuevo puede que uses «Workflows» en vez de conectores.",
      ],
      url: "https://learn.microsoft.com/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook",
    },
    en: {
      title: "Microsoft Teams webhook",
      steps: [
        "In the Teams channel: «…» → Connectors → «Incoming Webhook» → Configure.",
        "Name it, create and copy the URL.",
        "On new Teams you may use «Workflows» instead of connectors.",
      ],
      url: "https://learn.microsoft.com/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook",
    },
  },
  "teamchat.discord": {
    es: {
      title: "Webhook de Discord",
      steps: [
        "En el canal: Editar canal → Integraciones → Webhooks.",
        "«Nuevo webhook» → «Copiar URL del webhook».",
      ],
      url: "https://support.discord.com/hc/articles/228383668-Intro-to-Webhooks",
    },
    en: {
      title: "Discord webhook",
      steps: [
        "In the channel: Edit Channel → Integrations → Webhooks.",
        "«New Webhook» → «Copy Webhook URL».",
      ],
      url: "https://support.discord.com/hc/articles/228383668-Intro-to-Webhooks",
    },
  },
  "teamchat.googleChat": {
    es: {
      title: "Webhook de Google Chat",
      steps: [
        "En el espacio de Google Chat: nombre del espacio → «Apps e integraciones».",
        "«Webhooks» → «Añadir webhook» → nómbralo y copia la URL.",
      ],
      url: "https://developers.google.com/chat/how-tos/webhooks",
    },
    en: {
      title: "Google Chat webhook",
      steps: [
        "In the Google Chat space: space name → «Apps & integrations».",
        "«Webhooks» → «Add webhook» → name it and copy the URL.",
      ],
      url: "https://developers.google.com/chat/how-tos/webhooks",
    },
  },
  "teamchat.weCom": {
    es: {
      title: "Webhook de WeCom (企业微信)",
      steps: [
        "En un grupo de WeCom: «Añadir robot de grupo» (Add Group Robot).",
        "Crea el robot y copia su Webhook URL.",
      ],
      url: "https://developer.work.weixin.qq.com/document/path/91770",
    },
    en: {
      title: "WeCom webhook",
      steps: [
        "In a WeCom group: «Add Group Robot».",
        "Create the robot and copy its Webhook URL.",
      ],
      url: "https://developer.work.weixin.qq.com/document/path/91770",
    },
  },
  "teamchat.mattermost": {
    es: {
      title: "Webhook de Mattermost",
      steps: [
        "Integraciones → «Incoming Webhooks» → «Add Incoming Webhook».",
        "Elige canal, guarda y copia la URL.",
        "(El admin debe tener los incoming webhooks habilitados.)",
      ],
      url: "https://developers.mattermost.com/integrate/webhooks/incoming/",
    },
    en: {
      title: "Mattermost webhook",
      steps: [
        "Integrations → «Incoming Webhooks» → «Add Incoming Webhook».",
        "Pick a channel, save and copy the URL.",
        "(The admin must have incoming webhooks enabled.)",
      ],
      url: "https://developers.mattermost.com/integrate/webhooks/incoming/",
    },
  },
  "teamchat.rocketChat": {
    es: {
      title: "Webhook de Rocket.Chat",
      steps: [
        "Administración → Integraciones → «Nueva integración» → «Incoming WebHook».",
        "Actívalo, elige canal, guarda y copia la «Webhook URL».",
      ],
      url: "https://docs.rocket.chat/docs/integrations",
    },
    en: {
      title: "Rocket.Chat webhook",
      steps: [
        "Administration → Integrations → «New Integration» → «Incoming WebHook».",
        "Enable it, pick a channel, save and copy the «Webhook URL».",
      ],
      url: "https://docs.rocket.chat/docs/integrations",
    },
  },
};

interface ChannelFieldHelpProps {
  id: string;
}

export const ChannelFieldHelp: React.FC<ChannelFieldHelpProps> = ({ id }) => {
  const [locale] = useLocaleState();
  const loc: Locale = locale === "en" ? "en" : "es";
  const entry = HELP[id];
  if (!entry) return null;
  const h = entry[loc];
  const linkLabel = loc === "en" ? "Open ↗" : "Abrir ↗";

  return (
    <HelpTooltip
      content={
        <Box sx={{ maxWidth: 360 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {h.title}
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.5 } }}>
            {h.steps.map((s, i) => (
              <Typography
                key={i}
                component="li"
                variant="body2"
                sx={{ lineHeight: 1.5, wordBreak: "break-word" }}
              >
                {s}
              </Typography>
            ))}
          </Box>
          {h.note && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1, lineHeight: 1.5 }}
            >
              {h.note}
            </Typography>
          )}
          <Link
            href={h.url}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{ display: "inline-block", mt: 1.25, wordBreak: "break-all" }}
          >
            {linkLabel}
          </Link>
        </Box>
      }
    />
  );
};

export default ChannelFieldHelp;
